import React from 'react';

import {Box} from '@material-ui/core';
import {useHistory} from 'react-router-dom';
import {useSnackbar} from 'notistack';

import {
    View,
    Field,
    EfeResponse,
    ShareholderForm,
    EfeAccordionValues,
    AddressServiceRequest,
    ShareholderServiceRequest,
} from 'commons/models';
import {
    Roles,
    Messages,
    ViewKeys,
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    SnackbarVariants,
} from 'commons/constants';
import {useAlert, useSpinner} from 'hooks';
import {ClientService} from 'services';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {fieldCatalogGetter, useFlowFieldSearch} from 'hooks/FlowHook';
import {clearUnusedFields, formatDateToISO, nextStep} from 'commons/utils/Functions';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import ShareholderPersonalInfoForm from './shareholder-personal-info-form/ShareholderPersonalInfoForm';
import {ShareholderInfo} from 'commons/types/Types';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

const PHYSICAL_PERSON_KEY = '1';
const MORAL_PERSON_KEY = '2';
function ShareholderPersonalInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, expedientId, fatherKycId, setKycId, kycId} = useMenuContext();
    const {
        percentageCompleted,
        setPercentageCompleted,
        shareholders,
        setShareholders,
        setTempShareholder,
        setTempShaholderActive,
        setShareholderPersonType,
    } = useRelatedPeopleContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [edit, setEdit] = React.useState<boolean>(false);
    const [auxShareholder, setAuxShareholder] = React.useState<any>(undefined);
    const [currentPercentage, setCurrentPercentage] = React.useState<number>(0);

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
    }, []);

    React.useEffect(() => {
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            ViewKeys.SHAREHOLDER_PHYSICAL_INFO === flow.data.expandKey
        ) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<ShareholderServiceRequest> =
                        await ClientService.getShareholderInformation(kycId);
                    if (response && response.data) {
                        let formValues: any = response.data;
                        const address = await getPersonalAddress(
                            kycId,
                            formValues.foreignCitizen,
                            1
                        );
                        if (address) {
                            formValues = {...formValues, ...address};
                            formValues.addressOpt = true;
                        }
                        setAuxShareholder(formValues);
                        setShareholderPersonType(response.data.personTypeKey || '');
                        setCurrentPercentage(response.data.participationPercent || 0);
                    }
                    setEdit(true);
                    setIsLoading(false);
                } catch (error) {
                    setEdit(true);
                    setIsLoading(false);
                    setAuxShareholder(undefined);
                }
            })();
        } else if (fatherKycId > 0 && kycId === -1 && !edit) {
            setEdit(true);
        }
    }, [kycId]);

    async function updateGlobalPercentage(percentage: number) {
        setPercentageCompleted(percentageCompleted - currentPercentage + Number(percentage));
    }

    function loadTempShareholder(request: ShareholderServiceRequest) {
        setTempShareholder({
            kycId: request.kycId ? request.kycId : 0,
            completeName: request.businessDescription ? request.businessDescription : '',
            participationPercentage: request.participationPercent
                ? request.participationPercent
                : 0,
            personTypeKey: request.personTypeKey || '',
        });
    }

    async function onSubmit(values: ShareholderForm) {
        const request: ShareholderServiceRequest = {...clearUnusedFields(values, fields)};
        const address: AddressServiceRequest = {...values};
        try {
            setIsLoading(true);
            request.expedientId = expedientId;
            request.fatherKycId = fatherKycId;
            request.viewFatherId = flow.data.fatherId;
            request.viewId = flow.data.viewId;
            request.rol = Roles.SHAREHOLDER;
            request.birthDate = formatDateToISO(values.birthDate);
            request.protocolizationDate = formatDateToISO(values.protocolizationDate);
            request.kycId = kycId;
            const response: EfeResponse<ShareholderServiceRequest> =
                await ClientService.saveShareholderData(request, request.personTypeKey);
            if (response && response.data) {
                setKycId(response.data.kycId ? response.data.kycId : 0);
                setAuxShareholder({request});
                setShareholderPersonType(request.personTypeKey || '');
                setCurrentPercentage(request.participationPercent || 0);
                updateGlobalPercentage(request.participationPercent || 0);
                enqueueSnackbar(Messages.SH_INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                if (values.addressOpt) {
                    await updateShareholderLocation(
                        response.data.kycId || 0,
                        address,
                        values.foreignCitizen
                    );
                }
                const path = nextStep(flow, menu, true);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    response.data.participationPercent = request.participationPercent;
                    validateShareholder(response.data, {...request, kycId: response.data.kycId});
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                    await updateShareholderList(request);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_SH_INFORMATION);
        }
    }

    async function validateShareholder(
        data: ShareholderServiceRequest,
        request: ShareholderServiceRequest
    ) {
        if (data.personTypeKey === MORAL_PERSON_KEY) {
            updateShareholderList(request);
            setTempShaholderActive(true);
            loadTempShareholder(data);
            updateModuleStep(false);
            history.push(HistoryRoutes.SHAREHOLDER_LEGAL_REPRESENTATIVE);
        } else {
            setKycId(-1);
            setTempShaholderActive(false);
            updateModuleStep(true);
            await updateShareholderList(request);
            flow.data.expandKey = Constants.MODULE_STEP;
        }
    }

    async function updateShareholderList(data: ShareholderServiceRequest) {
        const aux: Array<ShareholderInfo> = [...shareholders];
        const index = shareholders.findIndex((item) => item.kycId === data.kycId);
        if (index >= 0) {
            setShareholders([]);
            aux[index].participationPercentage = data.participationPercent || 0;
            setShareholders([...aux]);
        } else {
            addShareholder(data, data.personTypeKey ? data.personTypeKey : '');
        }
    }

    function addShareholder(request: ShareholderServiceRequest, tmpPersonType: string) {
        const tmpShareholders: Array<ShareholderInfo> = [...shareholders];
        const tmpShareholder: ShareholderInfo = {
            kycId: request.kycId ? request.kycId : 0,
            participationPercentage: request.participationPercent
                ? request.participationPercent
                : 0,
            completeName: '',
            personTypeKey: request.personTypeKey || '',
        };
        if (tmpPersonType === PHYSICAL_PERSON_KEY) {
            tmpShareholder.completeName = `${request.name} ${request.lastName} ${request.secondLastName}`;
        } else {
            tmpShareholder.completeName = `${request.businessDescription}`;
        }
        setShareholders([...tmpShareholders, tmpShareholder]);
    }

    async function getPersonalAddress(
        kyc: number,
        foreign: boolean,
        addressId: number
    ): Promise<AddressServiceRequest | undefined> {
        try {
            setIsLoading(true);
            const response: EfeResponse<AddressServiceRequest> =
                await ClientService.getPersonalAddress(kyc, foreign, addressId);
            setIsLoading(true);
            if (response && response.data) {
                return response.data;
            } else {
                return undefined;
            }
        } catch (error) {
            setIsLoading(true);
            return undefined;
        }
    }

    async function updateShareholderLocation(
        kycId: number,
        address: AddressServiceRequest,
        foreign: boolean
    ): Promise<AddressServiceRequest | undefined> {
        const request = {...address};
        request.kycId = kycId;
        request.viewId = flow.data.viewId;
        request.expedientId = expedientId;
        request.viewFatherId = flow.data.fatherId;
        try {
            const response: EfeResponse<AddressServiceRequest> = await ClientService.saveAddress(
                request,
                foreign
            );
            if (response && response.data) {
                return response.data;
            } else {
                return undefined;
            }
        } catch (error) {
            showAlertError(error, Messages.ERROR_SAVING_ADDRESS);
            return undefined;
        }
    }

    async function overrideFields(personTypeKey: string, view: View | undefined) {
        if (personTypeKey) {
            setShareholderPersonType(personTypeKey);
            if (view && view.fields) {
                setFields(view.fields);
            } else {
                setFields(viewFields);
            }
        }
    }

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: 'Informaci\u00F3n personal del Accionista',
        reactElement: (
            <Box mt={8}>
                {!isLoading && edit && (
                    <ShareholderPersonalInfoForm
                        viewId={flow.data.viewId}
                        fields={[
                            ...fieldCatalogGetter(
                                fields.filter((field) => ATTR_NULL !== field.attrName)
                            ),
                        ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                        submitHandler={onSubmit}
                        fieldHandler={overrideFields}
                        auxShareholder={auxShareholder}
                        currentPercentage={currentPercentage}
                        accumulatedPercentage={percentageCompleted}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: ViewKeys.SHAREHOLDER_PHYSICAL_INFO === flow.data.expandKey,
    };

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.SHAREHOLDER_INFO)}
                values={[accordion]}
            />
        </MainContent>
    );
}

export default ShareholderPersonalInfo;
